import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Howl } from "howler";
import { GAME_SOUNDS, SOUND_NAME } from "../../sounds";
import { soundManager } from "../../SoundManager";

export const HowlCache = {};
export const soundDurations = {};

export const loadSounds = createAsyncThunk(
  "sounds/loadSounds",
  async (_, { rejectWithValue }) => {
    // 🚀 PARALLEL LOADING - Load all sounds simultaneously
    const soundPromises = GAME_SOUNDS.sounds.map(async (sound) => {
      if (!HowlCache[sound.name]) {
        // Create Howl with sprite support
        const howlConfig = {
          src: [sound.url],
          loop: sound.loop || false,
          volume: sound.volume || 1.0,
        };

        // Add sprite configuration if present
        if (sound.sprite) {
          howlConfig.sprite = sound.sprite;
        }

        HowlCache[sound.name] = new Howl(howlConfig);

        try {
          await new Promise((resolve, reject) => {
            const howl = HowlCache[sound.name];

            // Add more comprehensive error handling
            howl.once("load", () => {
              soundDurations[sound.name] = howl.duration();

              // If this is a sprite, also store individual sprite durations
              if (sound.sprite) {
                Object.entries(sound.sprite).forEach(([spriteName, [start, duration]]) => {
                  soundDurations[spriteName] = duration / 1000; // Convert ms to seconds
                });
              }

              resolve();
            });

            howl.once("loaderror", (id, error) => {
              reject(
                new Error(`Failed to load sound: ${sound.name} - ${error}`)
              );
            });

            // Set a timeout to catch hanging loads
            setTimeout(() => {
              if (howl.state() === "loading") {
                reject(new Error(`Sound loading timeout: ${sound.name}`));
              }
            }, 10000); // 10 second timeout
          });
        } catch (error) {
          throw error; // Let Promise.all catch this
        }
      }
    });

    // Wait for all sounds to finish loading (happens in parallel!)
    try {
      await Promise.all(soundPromises);
    } catch (error) {
      return rejectWithValue(`Failed to load some sounds`);
    }

    return {
      soundKeys: Object.keys(HowlCache),
      durations: soundDurations,
    };
  }
);

const soundsSlice = createSlice({
  name: "sounds",
  initialState: {
    soundEffectsEnabled: true,
    backgroundMusicEnabled: false,
    globalSoundDisabled: false,
    masterVolume: 1.0, // Master volume (0.0 to 2.0, default at 1.0 = center)
    currentBackgroundMusic: SOUND_NAME.background_music,
    soundKeys: [],
    soundVolumes: {},
    status: "idle",
    error: null,
  },
  reducers: {
    toggleSoundEffects(state) {
      state.soundEffectsEnabled = !state.soundEffectsEnabled;

      if (state.soundEffectsEnabled) {
        state.globalSoundDisabled = false;
      }

      Object.entries(HowlCache).forEach(([key, sound]) => {
        if (
          key !== SOUND_NAME.background_music &&
          key !== SOUND_NAME.free_spin_music &&
          key !== SOUND_NAME.feature_background_music &&
          key !== SOUND_NAME.ambient_music
        ) {
          sound.mute(!state.soundEffectsEnabled || state.globalSoundDisabled);
        }
      });

      // Sync SoundManager's internal state with Redux state
      soundManager.soundEffectsEnabled = state.soundEffectsEnabled;
      soundManager.globalSoundDisabled = state.globalSoundDisabled;
    },

    toggleBackgroundMusic(state) {
      state.backgroundMusicEnabled = !state.backgroundMusicEnabled;

      if (state.backgroundMusicEnabled) {
        state.globalSoundDisabled = false;
      }

      const regularMusic = HowlCache[SOUND_NAME.background_music];
      const freeSpinMusic = HowlCache[SOUND_NAME.free_spin_music];
      const featureMusic = HowlCache[SOUND_NAME.feature_background_music];
      const ambientMusic = HowlCache[SOUND_NAME.ambient_music];

      if (regularMusic) regularMusic.stop();
      if (freeSpinMusic) freeSpinMusic.stop();
      if (featureMusic) featureMusic.stop();

      if (state.backgroundMusicEnabled && !state.globalSoundDisabled) {
        const currentMusic = HowlCache[state.currentBackgroundMusic];
        if (currentMusic) {
          currentMusic.mute(false);
          currentMusic.play();
        }
        // Start ambient music alongside background music
        if (ambientMusic) {
          ambientMusic.mute(false);
          ambientMusic.play();
        }
      } else {
        // Stop ambient music when background music is disabled
        if (ambientMusic) ambientMusic.stop();
      }

      // Sync SoundManager's internal state with Redux state
      soundManager.backgroundMusicEnabled = state.backgroundMusicEnabled;
      soundManager.globalSoundDisabled = state.globalSoundDisabled;
    },

    toggleGlobalSound(state, action) {
      state.globalSoundDisabled = !state.globalSoundDisabled;

      // CRITICAL: Sync SoundManager FIRST before manipulating HowlCache
      // This ensures soundManager.play() checks use the correct state immediately
      soundManager.toggleGlobal(state.globalSoundDisabled);

      if (state.globalSoundDisabled) {
        state.soundEffectsEnabled = false;
        state.backgroundMusicEnabled = false;
        // SoundManager.toggleGlobal already muted and stopped all sounds, but ensure everything is stopped
        Object.values(HowlCache).forEach((sound) => {
          sound.mute(true);
          if (sound.playing()) sound.stop();
        });
      } else {
        state.soundEffectsEnabled = true;
        state.backgroundMusicEnabled = true;
        Object.entries(HowlCache).forEach(([key, sound]) => {
          sound.mute(false);
        });

        // Determine which music to play based on game state (passed from component)
        const { isInHoldAndWin } = action.payload || {};
        const musicToPlay = isInHoldAndWin
          ? SOUND_NAME.feature_background_music
          : SOUND_NAME.background_music;

        // Update current background music reference
        state.currentBackgroundMusic = musicToPlay;
        soundManager.currentBackgroundMusic = musicToPlay;

        // Play the appropriate music
        const currentMusic = HowlCache[musicToPlay];
        const ambientMusic = HowlCache[SOUND_NAME.ambient_music];
        if (currentMusic) {
          currentMusic.play();
        }
        if (ambientMusic) {
          ambientMusic.play();
        }
      }
    },

    setMasterVolume(state, action) {
      // Clamp volume between 0 and 2 (allows boosting up to 200%)
      state.masterVolume = Math.max(0, Math.min(2, action.payload));

      // Apply volume to all sounds in HowlCache
      Object.values(HowlCache).forEach((sound) => {
        sound.volume(state.masterVolume);
      });
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadSounds.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadSounds.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.soundKeys = action.payload.soundKeys;
        state.soundDurations = {
          ...state.soundDurations,
          ...action.payload.durations,
        };
      })
      .addCase(loadSounds.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const {
  toggleSoundEffects,
  toggleBackgroundMusic,
  toggleGlobalSound,
  setMasterVolume,
} = soundsSlice.actions;

export const selectMasterVolume = (state) => state.sound.masterVolume;

export default soundsSlice.reducer;
